/**
 * DEPOSIT (Phase 5 depth, FR-U-WAL-005). Add fiat to the wallet via a bank transfer or M-Pesa.
 *
 * Honest: the real deposit settles through an integrated bank or PSP on-ramp and is blocked on the
 * M-Pesa Daraja merchant approval (ADR 0003), so this is a preview. It shows the flow and the
 * instructions without moving money.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';

type Method = 'mpesa' | 'bank';
type Step = 'method' | 'amount' | 'instructions';

export default function Deposit() {
  const [method, setMethod] = useState<Method>('mpesa');
  const [amount, setAmount] = useState('');
  const [step, setStep] = useState<Step>('method');

  return (
    <div className="mx-auto max-w-md space-y-5">
      <Link
        to="/wallet"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
        </svg>
        Wallet
      </Link>

      <header>
        <h1 className="text-page-title text-ink">Add money</h1>
        <p className="mt-0.5 text-sm text-ink-muted">Top up your KES balance.</p>
      </header>

      {step === 'method' ? (
        <div className="space-y-3">
          <MethodCard
            active={method === 'mpesa'}
            onClick={() => setMethod('mpesa')}
            title="M-Pesa"
            sub="Pay from your M-Pesa to top up instantly"
          />
          <MethodCard
            active={method === 'bank'}
            onClick={() => setMethod('bank')}
            title="Bank transfer"
            sub="Transfer from any Kenyan bank account"
          />
          <button
            onClick={() => setStep('amount')}
            className="w-full rounded-field bg-primary py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-light"
          >
            Continue
          </button>
        </div>
      ) : null}

      {step === 'amount' ? (
        <div className="space-y-4 rounded-xl border border-border bg-surface p-6 shadow-card">
          <label htmlFor="amt" className="block text-sm font-medium text-ink">
            How much?
          </label>
          <div className="flex items-center gap-2 rounded-field border border-border px-3 py-2.5 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/25">
            <span className="text-sm text-ink-muted">KES</span>
            <input
              id="amt"
              autoFocus
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
              placeholder="0.00"
              className="w-full bg-transparent font-mono text-base text-ink outline-none"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {['500', '1000', '5000', '10000'].map((v) => (
              <button
                key={v}
                onClick={() => setAmount(v)}
                className="rounded-pill border border-border px-3 py-1 text-sm text-ink-muted transition hover:border-primary/40 hover:text-ink"
              >
                {Number(v).toLocaleString()}
              </button>
            ))}
          </div>
          <button
            onClick={() => setStep('instructions')}
            disabled={!amount}
            className="w-full rounded-field bg-primary py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-light disabled:opacity-50"
          >
            Continue
          </button>
        </div>
      ) : null}

      {step === 'instructions' ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-surface p-6 shadow-card">
            <p className="text-sm text-ink-muted">Deposit</p>
            <p className="tabular text-3xl font-semibold text-ink">KES {amount}</p>
            <div className="mt-4 space-y-3 border-t border-border pt-4 text-sm">
              {method === 'mpesa' ? (
                <>
                  <Row label="Pay bill" value="247247" mono />
                  <Row label="Account number" value="LIPA-7842" mono />
                </>
              ) : (
                <>
                  <Row label="Bank" value="KCB Bank" />
                  <Row label="Account" value="1234567890" mono />
                  <Row label="Reference" value="LIPA-7842" mono />
                </>
              )}
            </div>
          </div>
          <div className="rounded-card border border-info/30 bg-info/5 p-3 text-sm text-ink-muted">
            <span className="font-medium text-info">Preview.</span> Fiat deposit rails activate in a
            later sandbox phase. When live, your balance updates the moment the transfer is
            confirmed.
          </div>
          <Link
            to="/wallet"
            className="flex w-full items-center justify-center rounded-field border border-border py-3 text-sm font-medium text-ink transition hover:bg-surface-sunken"
          >
            Done
          </Link>
        </div>
      ) : null}
    </div>
  );
}

function MethodCard({
  active,
  onClick,
  title,
  sub,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  sub: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-card border p-4 text-left transition ${active ? 'border-primary bg-primary-tint' : 'border-border bg-surface hover:bg-surface-sunken'}`}
    >
      <span
        className={`flex h-10 w-10 items-center justify-center rounded-full ${active ? 'bg-primary/15 text-brand' : 'bg-surface-sunken text-ink-muted'}`}
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.8}
            d="M12 5v14m-7-7h14"
          />
        </svg>
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block text-sm font-medium ${active ? 'text-brand' : 'text-ink'}`}>
          {title}
        </span>
        <span className="block text-xs text-ink-muted">{sub}</span>
      </span>
    </button>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd className={`font-medium text-ink ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  );
}
